import React from 'react'
import { useSelector } from "react-redux";
import { Link, NavLink } from 'react-router-dom'
import { MdOutlineCancel } from 'react-icons/md'
import {
  FaUser,
  FaProjectDiagram,
  FaShoppingCart,
  FaLock,
  FaLeaf,
  FaShoppingBag,
  FaBookOpen,
  FaRocketchat
} from 'react-icons/fa';
import { BiSolidDashboard } from 'react-icons/bi';

import { useStateContext } from '../contexts/ContextProvider'

const Sidebar = () => {
  const { activeMenu, setActiveMenu, screenSize, currentMode, currentColor } = useStateContext()
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.role || 'siswa';

  // Colors
  const isDark = currentMode === 'Dark';
  const accentColor = currentColor || '#c9862a';
  const bgColor = isDark ? '#1a1a18' : '#f7f3ec';
  const borderColor = isDark ? '#2e2e28' : '#e8e0d0';
  const textPrimary = isDark ? '#f0ece3' : '#1a1a18';
  const textSecondary = isDark ? '#a09880' : '#6b6456';

  // Portfolio themed links configuration - based on user role
  const allLinks = [
    {
      title: 'Dashboard',
      links: [
        {
          name: 'dashboard',
          displayName: 'Dashboard',
          icon: <BiSolidDashboard />,
          allowedRoles: ['admin', 'guru', 'siswa']
        },
      ],
    },
    {
      title: 'Manajemen Portfolio',
      links: [
        {
          name: 'list-team',
          displayName: 'Team',
          icon: <FaUser />,
          allowedRoles: ['admin']
        },
        {
          name: 'list-program',
          displayName: 'Program',
          icon: <FaProjectDiagram />,
          allowedRoles: ['admin']
        },
        {
          name: 'list-produk',
          displayName: 'Produk',
          icon: <FaShoppingBag />,
          allowedRoles: ['admin']
        },
        {
          name: 'list-stories',
          displayName: 'Stories',
          icon: <FaBookOpen />,
          allowedRoles: ['admin']
        },
      ],
    },
    {
      title: 'Pengguna',
      links: [
        {
          name: 'list-komentar',
          displayName: 'Komentar',
          icon: <FaRocketchat />,
          allowedRoles: ['admin']
        },
      ],
    },
    {
      title: 'Sistem',
      links: [
        {
          name: 'ganti-password',
          displayName: 'Ubah Password',
          icon: <FaLock />,
          allowedRoles: ['admin', 'guru', 'siswa']
        },
      ],
    },
  ];

  // Filter links based on user role
  const filterLinksByRole = (links, role) => {
    const filteredCategories = links.map(category => {
      const filteredLinks = category.links.filter(link =>
        link.allowedRoles.includes(role)
      );

      return {
        ...category,
        links: filteredLinks
      };
    });

    return filteredCategories.filter(category => category.links.length > 0);
  };

  const links = filterLinksByRole(allLinks, userRole);

  const handleCloseSideBar = () => {
    if(activeMenu && screenSize <= 900){
      setActiveMenu(false)
    }
  }

  return (
    <div
      className="h-screen md:overflow-hidden overflow-auto md:hover:overflow-auto pb-10"
      style={{
        backgroundColor: bgColor,
        borderRight: `1px solid ${borderColor}`
      }}
    >
      {activeMenu && (<>
        {/* Header Section */}
        <div
          className={`flex justify-between items-center pb-4 border-b ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
          style={{ borderColor: borderColor }}
        >
          <Link
            to="/dashboard"
            onClick={handleCloseSideBar}
            className="items-center gap-3 ml-3 mt-4 flex text-lg font-bold tracking-tight transition-colors duration-300"
            style={{ color: textPrimary }}
          >
            {/* Logo Icon */}
            <div
              className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: accentColor }}
            >
              <FaLeaf className="text-white text-xs" />
            </div>
            <div className="flex flex-col">
              <span className="font-serif leading-tight">Rumoh Pangan</span>
              <span className="text-xs font-bold" style={{ color: accentColor }}>Aceh</span>
            </div>
          </Link>

          <button
            type='button'
            onClick={() => setActiveMenu((prevActiveMenu) => !prevActiveMenu)}
            className="text-xl rounded-lg p-2 mt-4 mr-3 block md:hidden transition-colors duration-300"
            style={{ color: textSecondary }}
          >
            <MdOutlineCancel />
          </button>
        </div>

        {/* Navigation Links */}
        <div className='mt-6 px-2'>
          {links.map((item) => (
            <div key={item.title} className="mb-5">
              {/* Section Header */}
              <p
                className="text-[10px] font-bold uppercase tracking-[2px] mx-4 mb-3"
                style={{ color: textSecondary }}
              >
                {item.title}
              </p>

              {/* Navigation Items */}
              {item.links.map((link) => (
                <NavLink
                  to={`/${link.name}`}
                  key={link.name}
                  onClick={handleCloseSideBar}
                  end
                  style={({ isActive }) => {
                    if (isActive) {
                      return {
                        backgroundColor: accentColor,
                        color: 'white',
                        borderRadius: '8px',
                        fontWeight: '500',
                      };
                    }
                    return {
                      color: textSecondary,
                      backgroundColor: 'transparent',
                    };
                  }}
                  className={({ isActive }) =>
                    `flex items-center gap-3 pl-4 pt-3 pb-3 rounded-lg text-sm m-2 transition-all duration-300 ${
                      isActive ? 'text-white shadow-md' : ''
                    }`
                  }
                >
                  <span className="text-base">{link.icon}</span>
                  <span className='font-medium'>{link.displayName}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      </>)}
    </div>
  )
}

export default Sidebar